import { Button } from '@/components/shadcn/button';
import {
  signOut,
  useSessionContext,
} from 'supertokens-auth-react/recipe/session';
import SettingsTabTitle from '../settings-tab-title';
import {
  useCurrentUser,
  useUsers,
  useImpersonation,
  useIsImpersonated,
  hasAdminRole,
} from '@/lib/auth-service';
import { InputGroup } from '../../input-group';
import { gql } from '@/__generated__';
import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import { logError } from '@/lib/rum';
import { toast } from '@/components/shadcn/use-toast';
import { useOrganizationContext } from '@/contexts/organization-context';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/shadcn/select';

const SetUserNameMutation = gql(`
  mutation SetUserNameMutation($input: SetUserNameInput!) {
    user {
      info {
        setName(input: $input) {
          id
          info {
            firstName
            lastName
          }
        }
      }
    }
  }
`);

export function ProfileTab() {
  const session = useSessionContext();
  const isAdmin =
    !session.loading &&
    session.doesSessionExist &&
    hasAdminRole(session.accessTokenPayload['st-role']?.v);

  const { t } = useTranslation();
  const { isLoading, user, updateUser } = useCurrentUser();
  const { organizationId } = useOrganizationContext();
  const { users } = useUsers(organizationId as string, isAdmin);
  const { impersonateUser, revertImpersonation } = useImpersonation(
    organizationId as string
  );
  const isImpersonated = useIsImpersonated();
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [nameLoading, setNameLoading] = useState<boolean>(true);
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [updateUserMetadata] = useMutation(SetUserNameMutation, {
    onCompleted: () => {
      updateUser();
    },
  });

  useEffect(() => {
    if (user) {
      setFirstName(user?.metadata?.firstName ?? '');
      setLastName(user?.metadata?.lastName ?? '');
      setNameLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!updateUser) {
      return;
    }
    updateUser();
  }, [session, updateUser]);

  const saveOnInputBlur = async () => {
    setNameLoading(true);
    try {
      const res = await updateUserMetadata({
        variables: {
          input: {
            firstName: firstName?.trim(),
            lastName: lastName?.trim(),
          },
        },
      });
      setFirstName(res.data?.user.info.setName.info?.firstName || '');
      setLastName(res.data?.user.info.setName.info?.lastName || '');
    } catch (error: any) {
      logError('Failed to update user metadata', { error });
      toast({
        description: `An error occurred while updating your name information: ${error.message}`,
        duration: 3000,
      });
    } finally {
      setNameLoading(false);
    }
  };

  const handleImpersonateUser = async () => {
    try {
      await impersonateUser(selectedUserId);
      toast({
        title: 'Success',
        description: 'Successfully impersonating user',
      });
    } catch (error) {
      logError('Failed to impersonate user', error);
      toast({
        title: 'Error',
        description: 'Failed to impersonate user',
        variant: 'destructive',
      });
    }
  };

  const handleRevertImpersonation = async () => {
    try {
      await revertImpersonation();
      toast({
        title: 'Success',
        description: 'Successfully reverted impersonation',
      });
    } catch (error) {
      logError('Failed to revert impersonation', error);
      toast({
        title: 'Error',
        description: 'Failed to revert impersonation',
        variant: 'destructive',
      });
    }
  };

  return (
    <div>
      <SettingsTabTitle title='Profile' />
      <div className='flex max-w-full flex-wrap justify-between rounded border border-gray-100 px-3 py-6'>
        <span className='leading-8'>{isLoading ? '' : user.primaryEmail}</span>
        <Button
          variant={'outline-destructive'}
          onClick={async () => await signOut()}
        >
          Logout
        </Button>
      </div>
      <div className='mt-4 flex flex-col gap-4'>
        <InputGroup
          className='mt-1'
          labelTitle={t('account-menu-settings.profile-first-name')}
          placeholder={t(
            'account-menu-settings.profile-first-name-placeholder'
          )}
          value={firstName}
          disabled={nameLoading}
          onChange={(e) => setFirstName(e.target.value)}
          onBlur={saveOnInputBlur}
        />
        <InputGroup
          className='mt-1'
          labelTitle={t('account-menu-settings.profile-last-name')}
          placeholder={t('account-menu-settings.profile-last-name-placeholder')}
          value={lastName}
          disabled={nameLoading}
          onChange={(e) => setLastName(e.target.value)}
          onBlur={saveOnInputBlur}
        />

        {(isAdmin || isImpersonated) && (
          <div className='mt-6 space-y-4 rounded-lg border border-gray-100 p-4'>
            <h3 className='text-lg font-medium'>User Impersonation</h3>
            {isImpersonated ? (
              <div>
                <p className='mb-2 text-sm text-gray-600'>
                  You are currently impersonating a user
                </p>
                <Button onClick={handleRevertImpersonation} variant='secondary'>
                  Revert Impersonation
                </Button>
              </div>
            ) : (
              <div className='space-y-2'>
                <p className='text-sm text-gray-600'>
                  Select a user to impersonate
                </p>
                <div className='flex gap-2'>
                  <Select
                    value={selectedUserId}
                    onValueChange={setSelectedUserId}
                  >
                    <SelectTrigger className='w-[200px]'>
                      <SelectValue placeholder='Select a user' />
                    </SelectTrigger>
                    <SelectContent>
                      {users
                        ?.filter((u) => u.id && !hasAdminRole(u.roles || []))
                        .map((user) => (
                          <SelectItem key={user.id} value={user.id as string}>
                            {user.email}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={handleImpersonateUser}
                    disabled={!selectedUserId}
                  >
                    Impersonate
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
